import { useMsal } from "@azure/msal-react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from "@material-ui/icons";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import { Autocomplete } from "@material-ui/lab";
import React, { ReactElement, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { globalStyles } from "../../../assets/globalStyles";
import {
  selectDatenklassen,
  selectIsMessstellenLoading,
  selectMessstellen,
  selectSelectedDatenklasse,
  selectSelectedMessstellen,
  setSelectedDatenklasse,
  setDatenklassen,
  selectIsDatenklassenLoading,
  setSelectedMessstellen,
  getMessstellen,
  getDatenklassen,
  selectStartDatum,
  selectEndeDatum,
  setStartDatum,
  setEndeDatum,
  getMesswertLogs,
  selectIsMesswertLogsLoading,
  selectMesswertLogs,
  startDataCollector,
  startIrregularDataChecker,
  startCorrelationCalculator,
  startQuantileCalculator,
  selectSelectedMessstellenGroups,
  setSelectedMessstellenGroups,
  selectFilteredMessstellenOfGroup,
  startAnomalyCalculator,
  startTrendsCalculator,
} from "./lambdacontrolSlice";
import { useStyles } from "./lambdacontrolStyle";
import {
  KeyboardDateTimePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { de } from "date-fns/locale";
import { selectDate } from "../../../features/konfiguration/konfigurationSlice";
import {
  addTimezoneOffset,
  nanosecondsTimeStampToLocalStringWithoutSlice,
  nanosecondsTimeStampToUTCStringWithoutSlice,
  subtractTimezoneOffset,
} from "../../../utils/utils";
import {
  DataGrid,
  deDE,
  GridCellParams,
  GridColDef,
  GridValueGetterParams,
  LocalizationV4,
} from "@material-ui/data-grid";
import {
  getMessstellenLookup,
  selectMessstellenDictionary,
} from "../../../features/messstellenLookup/messstellenLookupSlice";
import { getMessstellenForLookup } from "./../../../features/messstellenLookup/messstellenLookupSlice";
import { DataCollectorRequestType } from "./lambdacontrolTypes";


export default function LambdaControl(): ReactElement {
  const { instance } = useMsal();
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getMessstellen());
    dispatch(getMessstellenForLookup());
  }, [instance]);

  const messstellen = useAppSelector(selectMessstellen);
  const isMessstellenLoading = useAppSelector(selectIsMessstellenLoading);
  const selectedMessstellen = useAppSelector(selectSelectedMessstellen);
  const selectedMessstellenGroups = useAppSelector(
    selectSelectedMessstellenGroups
  );
  const datenklassen = useAppSelector(selectDatenklassen);
  const isDatenklassenLoading = useAppSelector(selectIsDatenklassenLoading);
  const selectedDatenklasse = useAppSelector(selectSelectedDatenklasse);
  const startDatum = useAppSelector(selectStartDatum);
  const endeDatum = useAppSelector(selectEndeDatum);
  const messwertLogs = useAppSelector(selectMesswertLogs);
  const isMesswertLogsLoading = useAppSelector(selectIsMesswertLogsLoading);

  const isUTC = useAppSelector(selectDate);
  const messstellenDict = useAppSelector(selectMessstellenDictionary);

  const [selectedMessstellenInputValue, setSelectedMessstellenInputValue] = React.useState("");
  const [selectedDatenklasseInputValue, setSelectedDatenklasseInputValue] = React.useState("");
  const [openMessstellenGroups, setOpenMessstellenGroups] = React.useState<string[]>([]);
  const [datacollectorButtonAnchorEl, setDatacollectorButtonAnchorEl] = React.useState<null | HTMLElement>(null);

  const classes = {
    ...globalStyles(),
    ...useStyles(),
  };

  const dataGridCols: GridColDef[] = [
    {
      field: "umspannwerk",
      headerName: "Umspannwerk",
      flex: 0.6,

      valueGetter: (params: GridValueGetterParams) => {
        const messstellenName = String(
          params.getValue(params.id, "messstelle")
        );
        //eslint-disable-next-line security/detect-object-injection
        return messstellenDict[messstellenName] == undefined
          ? "Wird geladen..."
          : getMessstellenLookup(
            messstellenDict,
            messstellenName,
            "umspannwerk"
          );
      },
    },
    {
      field: "messstelle_schaltfeld",
      headerName: "Messstelle",
      flex: 0.8,

      valueGetter: (params: GridValueGetterParams) => {
        const messstellenName = String(
          params.getValue(params.id, "messstelle")
        );
        const fieldNameAsString: string =
          getMessstellenLookup(messstellenDict, messstellenName, "schaltfeld") +
          " (" +
          messstellenName +
          ")";
        //eslint-disable-next-line security/detect-object-injection
        return messstellenDict[messstellenName] == undefined
          ? "Wird geladen..."
          : fieldNameAsString;
      },
    },
    {
      field: "start",
      headerName: "Start",
      flex: 0.5,
      filterable: false,
      renderCell: function start(params: GridCellParams) {
        return (
          <React.Fragment>
            {isUTC
              ? nanosecondsTimeStampToUTCStringWithoutSlice(params.row.start)
              : nanosecondsTimeStampToLocalStringWithoutSlice(params.row.start)}
            <IconButton
              onClick={() =>
                dispatch(setStartDatum(new Date(params.row.start / 1000000)))
              }
              color="secondary"
              style={{ fontSize: "medium" }}
            >
              Start
            </IconButton>
            <IconButton
              onClick={() =>
                dispatch(setEndeDatum(new Date(params.row.start / 1000000)))
              }
              color="secondary"
              style={{ fontSize: "medium" }}
            >
              Ende
            </IconButton>
          </React.Fragment>
        );
      },
    },
    {
      field: "ende",
      headerName: "Ende",
      flex: 0.5,
      filterable: false,
      renderCell: function ende(params: GridCellParams) {
        return (
          <React.Fragment>
            {isUTC
              ? nanosecondsTimeStampToUTCStringWithoutSlice(params.row.ende)
              : nanosecondsTimeStampToLocalStringWithoutSlice(params.row.ende)}
            <IconButton
              onClick={() =>
                dispatch(setStartDatum(new Date(params.row.ende / 1000000)))
              }
              color="secondary"
              style={{ fontSize: "medium" }}
            >
              Start
            </IconButton>
            <IconButton
              onClick={() =>
                dispatch(setEndeDatum(new Date(params.row.ende / 1000000)))
              }
              color="secondary"
              style={{ fontSize: "medium" }}
            >
              Ende
            </IconButton>
          </React.Fragment>
        );
      },
    },
  ];

  return (
    <div> <Container maxWidth={false} style={{ marginTop: "10px" }}>
      <Grid container md={12} spacing={2} style={{ marginBottom: "10px" }}>
        <Grid item md={3} />
        <Grid item md={6}>
          <Typography color="primary" variant="h5">
            Lambda control
          </Typography>
        </Grid>
        <Grid item md={12}></Grid>
      </Grid>
    </Container>
      <Paper
        className={classes.paper}
        style={{
          marginLeft: "24px",
          marginRight: "24px",
          marginBottom: "10px",
        }}
      >
        <Grid container spacing={4}>
          <Grid item xs>
            <Autocomplete
              id="select-messwert"
              limitTags={1}
              multiple
              disableCloseOnSelect
              noOptionsText={
                <React.Fragment>
                  {isMessstellenLoading ? (
                    <CircularProgress style={{ padding: "5px" }} />
                  ) : (
                    "Keine Messstellen"
                  )}
                </React.Fragment>
              }
              value={selectedMessstellen}
              onChange={(_, newValue) => {
                dispatch(setSelectedMessstellen(newValue));
                dispatch(
                  setSelectedMessstellenGroups(
                    newValue
                      .filter((x) => {
                        const allMessstellenForThisUW = messstellen.filter(
                          (mv) => mv.umspannwerk == x.umspannwerk
                        ).length;
                        const selectedMessstellenForThisUW = newValue.filter(
                          (mv) => mv.umspannwerk == x.umspannwerk
                        ).length;
                        return (
                          allMessstellenForThisUW ==
                          selectedMessstellenForThisUW
                        );
                      })
                      .map((x) => x.umspannwerk)
                  )
                );
                if (newValue.length == 1) {
                  dispatch(getDatenklassen(newValue[0].name));
                } else {
                  dispatch(setDatenklassen([]));
                  setSelectedDatenklasseInputValue("");
                }
              }}
              onClose={() => {
                setSelectedMessstellenInputValue("");
              }}
              inputValue={selectedMessstellenInputValue}
              onInputChange={(_, newInputValue, reason) => {
                if (reason == "reset") return;
                setSelectedMessstellenInputValue(newInputValue);
              }}
              options={messstellen ? messstellen : []}
              getOptionLabel={(option) =>
                option.schaltfeld + " (" + option.name + ")"
              }
              groupBy={(option) => option.umspannwerk}
              renderGroup={(params) => {
                return messstellen.every((x) => x.umspannwerk != null) ? (
                  <React.Fragment>
                    <Checkbox
                      icon={<CheckBoxOutlineBlank fontSize="small" />}
                      checkedIcon={<CheckBox fontSize="small" />}
                      style={{ marginRight: -10 }}
                      checked={selectedMessstellenGroups.includes(params.group)}
                      indeterminate={
                        !selectedMessstellenGroups.includes(params.group) &&
                        selectedMessstellen.some(
                          (m) => m.umspannwerk == params.group
                        )
                      }
                      onChange={() => {
                        if (selectedMessstellenGroups.includes(params.group)) {
                          dispatch(
                            setSelectedMessstellenGroups(
                              selectedMessstellenGroups.filter(
                                (x) => x != params.group
                              )
                            )
                          );
                          dispatch(
                            selectFilteredMessstellenOfGroup({
                              groups: selectedMessstellenGroups.filter(
                                (x) => x != params.group
                              ),
                              filter: selectedMessstellenInputValue,
                            })
                          );
                        } else {
                          dispatch(
                            setSelectedMessstellenGroups(
                              selectedMessstellenGroups.concat([params.group])
                            )
                          );
                          dispatch(
                            selectFilteredMessstellenOfGroup({
                              groups: selectedMessstellenGroups.concat([
                                params.group,
                              ]),
                              filter: selectedMessstellenInputValue,
                            })
                          );
                        }
                      }}
                    />
                    <Button
                      style={{
                        fontWeight: "bold",
                        textTransform: "none",
                        fontSize: "medium",
                      }}
                      onClick={() =>
                        openMessstellenGroups.includes(params.group)
                          ? setOpenMessstellenGroups(
                            openMessstellenGroups.filter(
                              (x) => x != params.group
                            )
                          )
                          : setOpenMessstellenGroups(
                            openMessstellenGroups.concat([params.group])
                          )
                      }
                    >
                      {openMessstellenGroups.includes(params.group) ? (
                        <KeyboardArrowDown style={{ marginRight: 10 }} />
                      ) : (
                        <KeyboardArrowRight style={{ marginRight: 10 }} />
                      )}
                      {params.group}
                    </Button>
                    {openMessstellenGroups.includes(params.group)
                      ? params.children
                      : ""}
                    <br />
                  </React.Fragment>
                ) : (
                  params.children
                );
              }}
              renderOption={(option, { selected }) => (
                <React.Fragment>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    style={{
                      marginLeft: 12,
                      marginRight: 12,
                      paddingTop: 1,
                      paddingBottom: 1,
                    }}
                    checked={selected}
                  />
                  {option.schaltfeld + " (" + option.name + ")"}
                </React.Fragment>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Messstelle"
                  variant="outlined"
                  classes={{ root: classes.textFieldRoot }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        <div>
                          <Tooltip title="Alle Messstellenwerte selektieren">
                            <Button
                              size="small"
                              variant="contained"
                              disabled={isMessstellenLoading}
                              color="secondary"
                              onClick={() => {
                                dispatch(
                                  setSelectedMessstellenGroups(
                                    messstellen.map((x) => x.umspannwerk)
                                  )
                                );
                                dispatch(
                                  selectFilteredMessstellenOfGroup({
                                    groups: null,
                                    filter: selectedMessstellenInputValue,
                                  })
                                );
                                dispatch(setDatenklassen([]));
                                setSelectedDatenklasseInputValue("");
                              }}
                            >
                              <DoneAllIcon />
                              Alle
                            </Button>
                          </Tooltip>
                          {params.InputProps.endAdornment}
                        </div>
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              className={classes.selectEmpty}
            />
          </Grid>
          <Grid item xs>
            <Autocomplete
              id="select-datenklasse"
              noOptionsText={
                <React.Fragment>
                  {isDatenklassenLoading ? (
                    <CircularProgress style={{ padding: "5px" }} />
                  ) : (
                    "Keine Datenklassen"
                  )}
                </React.Fragment>
              }
              disabled={selectedMessstellen.length != 1}
              value={selectedDatenklasse}
              onChange={(_, newValue) => {
                if (newValue) {
                  dispatch(setSelectedDatenklasse(newValue));
                }
              }}
              inputValue={selectedDatenklasseInputValue}
              onInputChange={(event, newInputValue) => {
                setSelectedDatenklasseInputValue(newInputValue);
              }}
              options={datenklassen}
              getOptionLabel={(option) =>
                option.beschreibung != ""
                  ? option.hersteller + " " + option.beschreibung
                  : ""
              }
              renderInput={(params) => (
                <TextField {...params} label="Datenklasse" variant="outlined" />
              )}
              className={classes.selectEmpty}
              style={{
                marginLeft: "16px",
                marginRight: "16px",
                marginBottom: "20px",
              }}
            />
          </Grid>
          <Grid item xs>
            <MuiPickersUtilsProvider locale={de} utils={DateFnsUtils}>
              <KeyboardDateTimePicker
                value={
                  isUTC ? addTimezoneOffset(startDatum) : new Date(startDatum)
                }
                className={classes.datePicker}
                format="dd.MM.yyyy HH:mm:ss"
                label="Startzeitpunkt"
                okLabel="OK"
                cancelLabel="ABBRECHEN"
                inputVariant="outlined"
                ampm={false}
                disableFuture={true}
                invalidDateMessage={
                  "Das ausgewählte Datum hat ein ungültiges Format."
                }
                onChange={(event) => {
                  dispatch(
                    setStartDatum(
                      isUTC
                        ? subtractTimezoneOffset(new Date(event + ""))
                        : new Date(event + "")
                    )
                  );
                }}
              ></KeyboardDateTimePicker>
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs>
            <MuiPickersUtilsProvider locale={de} utils={DateFnsUtils}>
              <KeyboardDateTimePicker
                value={
                  isUTC ? addTimezoneOffset(endeDatum) : new Date(endeDatum)
                }
                className={classes.datePicker}
                format="dd.MM.yyyy HH:mm:ss"
                label="Endzeitpunkt"
                okLabel="OK"
                cancelLabel="ABBRECHEN"
                inputVariant="outlined"
                ampm={false}
                disableFuture={true}
                minDate={startDatum}
                minDateMessage="Das ausgewählte Datum liegt vor dem Startzeitpunkt"
                invalidDateMessage={
                  "Das ausgewählte Datum hat ein ungültiges Format."
                }
                onChange={(event) => {
                  dispatch(
                    setEndeDatum(
                      isUTC
                        ? subtractTimezoneOffset(new Date(event + ""))
                        : new Date(event + "")
                    )
                  );
                }}
              ></KeyboardDateTimePicker>
            </MuiPickersUtilsProvider>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(
                  getMesswertLogs(
                    selectedMessstellen.map((m) => m.name),
                    selectedDatenklasse
                  )
                );
              }}
              color="secondary"
              disabled={selectedMessstellen.length == 0}
            >
              MesswertLogs
            </Button>
          </Grid>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => setDatacollectorButtonAnchorEl(event.currentTarget)}
              color="secondary"
              disabled={
                selectedMessstellen.length == 0 ||
                startDatum.getTime() >= endeDatum.getTime()
              }
            >
              DataCollector <ArrowDropDown />
            </Button>
            <Menu
              id="simple-menu"
              anchorEl={datacollectorButtonAnchorEl}
              keepMounted
              open={Boolean(datacollectorButtonAnchorEl)}
              onClose={() => setDatacollectorButtonAnchorEl(null)}
              style={{ marginTop: "45px" }}
            >
              <MenuItem
                onClick={() => {
                  setDatacollectorButtonAnchorEl(null);
                  dispatch(startDataCollector(startDatum, endeDatum, selectedMessstellen.map((m) => m.name), DataCollectorRequestType.all));
                }}
              >
                Messwerte und Ereignisse
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDatacollectorButtonAnchorEl(null);
                  dispatch(startDataCollector(startDatum, endeDatum, selectedMessstellen.map((m) => m.name), DataCollectorRequestType.measurements));
                }}
              >
                Nur Messwerte
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDatacollectorButtonAnchorEl(null);
                  dispatch(startDataCollector(startDatum, endeDatum, selectedMessstellen.map((m) => m.name), DataCollectorRequestType.events));
                }}
              >
                Nur Ereignisse
              </MenuItem>
            </Menu>
          </Grid>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(
                  startIrregularDataChecker(
                    startDatum,
                    endeDatum,
                    selectedMessstellen.map((m) => m.name)
                  )
                );
              }}
              color="secondary"
              disabled={
                selectedMessstellen.length == 0 ||
                startDatum.getTime() >= endeDatum.getTime()
              }
            >
              IrregularDataChecker
            </Button>
          </Grid>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(
                  startQuantileCalculator(
                    startDatum,
                    endeDatum,
                    selectedMessstellen.map((m) => m.name)
                  )
                );
              }}
              color="secondary"
              disabled={
                selectedMessstellen.length == 0 ||
                startDatum.getTime() >= endeDatum.getTime()
              }
            >
              QuantileCalculator
            </Button>
          </Grid>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(
                  startCorrelationCalculator(
                    startDatum,
                    endeDatum,
                    selectedMessstellen.map((m) => m.name)
                  )
                );
              }}
              color="secondary"
              disabled={
                selectedMessstellen.length == 0 ||
                startDatum.getTime() >= endeDatum.getTime()
              }
            >
              CorrelationCalculator
            </Button>
          </Grid>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(
                  startAnomalyCalculator(
                    startDatum,
                    endeDatum,
                    selectedMessstellen.map((m) => m.name)
                  )
                );
              }}
              color="secondary"
              disabled={
                selectedMessstellen.length == 0 ||
                startDatum.getTime() >= endeDatum.getTime()
              }
            >
              AnomalyCalculator
            </Button>
          </Grid>
          <Grid item xs>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(
                  startTrendsCalculator(
                    startDatum,
                    endeDatum,
                    selectedMessstellen.map((m) => m.name)
                  )
                );
              }}
              color="secondary"
              disabled={
                selectedMessstellen.length == 0 ||
                startDatum.getTime() >= endeDatum.getTime()
              }
            >
              TrendsCalculator
            </Button>
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          <Grid item xs>
            <div style={{ height: "50vh", width: "100%" }}>
              <DataGrid
                localeText={
                  (deDE as LocalizationV4).props.MuiDataGrid.localeText
                }
                disableColumnMenu={false}
                disableColumnSelector={true}
                columns={dataGridCols}
                rows={messwertLogs}
                //   sortModel={sorting}
                //   onSortModelChange={(model) => setSorting(model.sortModel)}
                loading={isMesswertLogsLoading}
              />
            </div>
          </Grid>
        </Grid>
      </Paper> </div>
  );
}
